'use client';

import { Composite } from '@floating-ui/react';
import { type KeyboardEvent, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { PhotoCarouselRootContext } from './context/photo-carousel-root.context';
import { useDotsRange } from './hooks/use-dots-range';
import { PhotoCarouselDot } from './photo-carousel-dot';
import { photoCarouselDotsStyles } from './styles/photo-carousel-dots.styles';
import type { PhotoCarouselDotPropsInterface } from './types/photo-carousel-dot.types';
import type { PhotoCarouselDotsPropsInterface } from './types/photo-carousel-dots.types';

/**
 * Container for multiple `PhotoCarousel.Dot` components.
 *
 * Also handles keyboard interactions based on its `tablist` role.
 *
 * @since 8.66.0
 */
export const PhotoCarouselDots = forwardRef<HTMLDivElement, PhotoCarouselDotsPropsInterface>(
  (props: PhotoCarouselDotsPropsInterface, forwardedRef) => {
    const {
      activeIndex: activeIndexContext,
      id: idContext,
      shouldLoop,
      totalSlides: totalSlidesContext,
      updateActiveIndex,
    } = useContext(PhotoCarouselRootContext);

    const {
      activeIndex = activeIndexContext || 0,
      'aria-label': ariaLabel = 'Slides',
      className,
      'css': cssProp,
      maxDotsToDisplay = 5,
      onKeyDown,
      totalSlides = totalSlidesContext || 0,
      renderDot = ({ key, dotProps }) => <PhotoCarouselDot key={key} {...dotProps} />,
      ...rest
    } = props;

    const onTabsFocusChange = useCallback(
      (index: number) => {
        updateActiveIndex(index);
      },
      [updateActiveIndex],
    );

    const render = useCallback(
      (slideIndex: number, isActive: boolean, isContinuation: boolean, isHidden: boolean) => {
        const key = `dot-${slideIndex}`;

        const dotProps: PhotoCarouselDotPropsInterface = {
          'aria-controls': `${idContext}-slide-${slideIndex + 1}`,
          'aria-label': `Slide ${slideIndex + 1}`,
          'aria-selected': isActive,
          isContinuation,
          isHidden,
          'tabIndex': isActive ? 0 : -1,
        };

        return renderDot({ dotProps, key });
      },
      [renderDot, idContext],
    );

    const dotsToRender = useDotsRange({
      activeIndex,
      totalSlides,
      maxDotsToDisplay,
    });

    const content = dotsToRender.map(({ i, isActive, isContinuation, isHidden }) =>
      render(i, isActive, isContinuation, isHidden),
    );

    /**
     * Adds support for Home and End keys on PC as recommended by W3C:
     * https://www.w3.org/WAI/ARIA/apg/patterns/carousel/examples/carousel-2-tablist/#kbd_label
     * (floating-ui provides the arrow key support)
     */
    const handleKeyDown = useCallback(
      (event: KeyboardEvent<Element>) => {
        let flag = false;

        switch (event.key) {
          case 'Home': {
            updateActiveIndex?.(0);
            flag = true;
            break;
          }

          case 'End': {
            updateActiveIndex?.(totalSlides - 1);
            flag = true;
            break;
          }

          default: {
            break;
          }
        }

        if (flag) {
          event.stopPropagation();
          event.preventDefault();
        }

        onKeyDown?.(event);
      },
      [totalSlides, updateActiveIndex, onKeyDown],
    );

    const styles = css(photoCarouselDotsStyles, cssProp);

    {
      /* Intentionally not allowing user overrides for Composite */
    }
    return (
      <Composite loop={shouldLoop} activeIndex={activeIndex} onNavigate={onTabsFocusChange}>
        <div
          aria-label={ariaLabel}
          role="tablist"
          ref={forwardedRef}
          data-c11n-component="PhotoCarousel.Dots"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          onKeyDown={handleKeyDown}
          {...rest}
        >
          {content}
        </div>
      </Composite>
    );
  },
);

PhotoCarouselDots.displayName = 'PhotoCarousel.Dots';
